import React from "react";
import {
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonItem,
  IonLabel,
  IonBadge,
  IonList,
  IonButton,
  //IonInput,
  //IonGrid,
  //IonRow,
  //IonCol,
} from "@ionic/react";
import { useQuery } from "react-query";
import { useHistory } from "react-router-dom";
import { format } from "date-fns";
import api from "@/services/api";
import { useI18n } from "@/i18n";

interface Prediction {
  id: number;
  match_id: number;
  predicted_home_score: number;
  predicted_away_score: number;
  points_earned: number;
  created_at: string;
  kickoff_time: string;
  home_team_name: string;
  away_team_name: string;
  home_score: number | null;
  away_score: number | null;
  status: string;
}

const Predictions: React.FC = () => {
  const history = useHistory();
  const { t } = useI18n();
  const {
    data: predictions,
    isLoading,
    error,
  } = useQuery<{ predictions: Prediction[] }>(["predictions"], async () => {
    const response = await api.get("/api/users/predictions");
    return response.data;
  });

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return t("invalidDate");
      }
      return format(date, "MMM dd, yyyy HH:mm");
    } catch (error) {
      console.error("Error formatting date:", dateString, error);
      return t("invalidDate");
    }
  };

  const getPredictionStatus = (prediction: Prediction) => {
    if (prediction.status === "finished" && prediction.home_score !== null && prediction.away_score !== null) {
      if (
        prediction.home_score === prediction.predicted_home_score &&
        prediction.away_score === prediction.predicted_away_score
      ) {
        return { text: t("predictionPerfect"), color: "success" };
      } else if (
        (prediction.home_score > prediction.away_score &&
          prediction.predicted_home_score > prediction.predicted_away_score) ||
        (prediction.home_score < prediction.away_score &&
          prediction.predicted_home_score < prediction.predicted_away_score) ||
        (prediction.home_score === prediction.away_score &&
          prediction.predicted_home_score === prediction.predicted_away_score)
      ) {
        return { text: t("predictionCorrect"), color: "primary" };
      } else {
        return { text: t("predictionWrong"), color: "danger" };
      }
    }
    return { text: t("pending"), color: "medium" };
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{t("predictionsTitle")}</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen>
        <IonHeader collapse="condense">
          <IonToolbar>
            <IonTitle size="large">{t("predictionsTitle")}</IonTitle>
          </IonToolbar>
        </IonHeader>

        <div className="ion-padding">
          {isLoading ? (
            <IonCard>
              <IonCardContent>
                <p>{t("loadingPredictions")}</p>
              </IonCardContent>
            </IonCard>
          ) : error ? (
            <IonCard>
              <IonCardContent>
                <p>{t("errorPredictions")}</p>
              </IonCardContent>
            </IonCard>
          ) : (
            <IonList>
              {predictions?.predictions?.map((prediction) => {
                const status = getPredictionStatus(prediction);
                return (
                  <IonCard key={prediction.id}>
                    <IonCardHeader>
                      <IonCardTitle>
                        {prediction.home_team_name} vs{" "}
                        {prediction.away_team_name}
                      </IonCardTitle>
                    </IonCardHeader>
                    <IonCardContent>
                      <IonItem>
                        <IonLabel>
                          <h2>{t("yourPrediction")}</h2>
                          <p>
                            {prediction.home_team_name}{" "}
                            {prediction.predicted_home_score} -{" "}
                            {prediction.predicted_away_score}{" "}
                            {prediction.away_team_name}
                          </p>
                        </IonLabel>
                      </IonItem>

                      {prediction.home_score !== null &&
                        prediction.away_score !== null && (
                          <IonItem>
                            <IonLabel>
                              <h2>{t("actualResult")}</h2>
                              <p>
                                {prediction.home_team_name}{" "}
                                {prediction.home_score} -{" "}
                                {prediction.away_score}{" "}
                                {prediction.away_team_name}
                              </p>
                            </IonLabel>
                          </IonItem>
                        )}

                      <IonItem>
                        <IonLabel>
                          <h2>{t("status")}</h2>
                          <IonBadge color={status.color}>
                            {status.text}
                          </IonBadge>
                        </IonLabel>
                      </IonItem>

                      {prediction.points_earned > 0 && (
                        <IonItem>
                          <IonLabel>
                            <h2>{t("pointsEarned")}</h2>
                            <p>
                              {prediction.points_earned} {t("points")}
                            </p>
                          </IonLabel>
                        </IonItem>
                      )}

                      <IonItem>
                        <IonLabel>
                          <h2>{t("matchDate")}</h2>
                          <p>{formatDate(prediction.kickoff_time)}</p>
                        </IonLabel>
                      </IonItem>

                      <IonItem>
                        <IonLabel>
                          <h2>{t("predictionMade")}</h2>
                          <p>{formatDate(prediction.created_at)}</p>
                        </IonLabel>
                      </IonItem>
                    </IonCardContent>
                  </IonCard>
                );
              })}

              {predictions?.predictions?.length === 0 && (
                <IonCard>
                  <IonCardContent>
                    <p>{t("noPredictions")}</p>
                    <IonButton
                      onClick={() => history.push("/matches")}
                      expand="block"
                      className="ion-margin-top"
                    >
                      {t("viewMatches")}
                    </IonButton>
                  </IonCardContent>
                </IonCard>
              )}
            </IonList>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default Predictions;
